const HOUR_MS = 60 * 60 * 1000;

export const RYTE_DEBUG_PAST_HOURS_PARAM = "rytePastHours";
export const RYTE_DEBUG_FUTURE_HOURS_PARAM = "ryteFutureHours";
export const DEBUG_TIME_RANGE_DEFAULT_HOURS = 120;
export const DEBUG_TIME_RANGE_MIN_HOURS = 12;
export const DEBUG_TIME_RANGE_MAX_HOURS = 120;
export const DATA_HISTORY_BUFFER_HOURS = 6;

export interface RyteTimeRangeConfig {
  chartPastHours: number;
  chartFutureHours: number;
  apiPastHours: number;
  apiFutureHours: number;
  isCustom: boolean;
}

export interface RyteTimeBounds extends RyteTimeRangeConfig {
  chartStartMs: number;
  chartEndMs: number;
  apiStartMs: number;
  apiEndMs: number;
}

interface ResolveRyteTimeRangeParams {
  isDebugMode: boolean;
  pastHours: number | null | undefined;
  futureHours: number | null | undefined;
}

export const DEFAULT_RYTE_TIME_RANGE: RyteTimeRangeConfig = {
  chartPastHours: 12,
  chartFutureHours: 12,
  apiPastHours: 18,
  apiFutureHours: 12,
  isCustom: false,
};

export const isValidDebugTimeRangeHours = (hours: number): boolean =>
  Number.isInteger(hours) &&
  hours >= DEBUG_TIME_RANGE_MIN_HOURS &&
  hours <= DEBUG_TIME_RANGE_MAX_HOURS;

export const resolveRyteTimeRange = ({
  isDebugMode,
  pastHours,
  futureHours,
}: ResolveRyteTimeRangeParams): RyteTimeRangeConfig => {
  if (
    !isDebugMode ||
    pastHours == null ||
    futureHours == null ||
    !isValidDebugTimeRangeHours(pastHours) ||
    !isValidDebugTimeRangeHours(futureHours)
  ) {
    return DEFAULT_RYTE_TIME_RANGE;
  }

  return {
    chartPastHours: pastHours,
    chartFutureHours: futureHours,
    apiPastHours: pastHours + DATA_HISTORY_BUFFER_HOURS,
    apiFutureHours: futureHours,
    isCustom: true,
  };
};

export const buildRyteTimeBounds = (
  range: RyteTimeRangeConfig,
  referenceNowMs: number
): RyteTimeBounds => ({
  ...range,
  chartStartMs: referenceNowMs - range.chartPastHours * HOUR_MS,
  chartEndMs: referenceNowMs + range.chartFutureHours * HOUR_MS,
  apiStartMs: referenceNowMs - range.apiPastHours * HOUR_MS,
  apiEndMs: referenceNowMs + range.apiFutureHours * HOUR_MS,
});

export const buildDebugTimeRangeUrl = (
  href: string,
  pastHours: number,
  futureHours: number
): string => {
  const url = new URL(href);
  url.searchParams.set("debugMode", "true");
  url.searchParams.set(RYTE_DEBUG_PAST_HOURS_PARAM, String(pastHours));
  url.searchParams.set(RYTE_DEBUG_FUTURE_HOURS_PARAM, String(futureHours));
  return url.toString();
};

export const buildDebugTimeRangeResetUrl = (href: string, disableDebugMode = false): string => {
  const url = new URL(href);
  url.searchParams.delete(RYTE_DEBUG_PAST_HOURS_PARAM);
  url.searchParams.delete(RYTE_DEBUG_FUTURE_HOURS_PARAM);
  if (disableDebugMode) url.searchParams.delete("debugMode");
  return url.toString();
};
